import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { PageContainer } from '../../../../../components/page-container/page-container'
import { Loading } from '../../../../../components/loading/loading'
import { Error } from '../../../../../components/error/error'
import { paths } from '../../../../../router/paths'
import { styles, texts } from './closure-detail.styles'
import { formatCurrencyGTQ } from '../../../../../utils/formatters'
import { Button } from '../../../../../components/button'
import { PrintReceiptModal } from '../../../../../components/print-receipt-modal/print-receipt-modal'

// Interface local para evitar conflitos de importação
interface ClosureDetailType {
  journeyId: number
  userId: number
  company: string
  routeId: number
  totalAmount: number
  closingTime: string
  status: string
  creationDate: string
  updateDate: string
  responsible?: string
  routeName?: string
  companyName?: string
  userName?: string
}

export const ClosureDetail = () => {
  const navigate = useNavigate()
  const location = useLocation() as any
  const journeyFromState = location?.state?.journey

  const [closureDetail, setClosureDetail] = useState<ClosureDetailType | null>(
    journeyFromState || null,
  )
  const [loading, setLoading] = useState(!journeyFromState)
  const [error, setError] = useState<string | null>(null)

  // Carregar dados do cierre
  useEffect(() => {
    if (!closureDetail && journeyFromState) {
      setClosureDetail(journeyFromState)
      setLoading(false)
    }
    // Se não houver dados no state (acesso direto via URL), podemos decidir futuramente buscar via API.
    if (!journeyFromState && !closureDetail) {
      setError('Datos del cierre no encontrados. Vuelve e inténtalo de nuevo.')
      setLoading(false)
    }
  }, [journeyFromState, closureDetail])

  const handleGoBack = () => {
    navigate(paths.previousClosings)
  }

  const [printOpen, setPrintOpen] = useState(false)
  const handlePrint = () => {
    // Abre modal para impressão/Exportação (journey)
    setPrintOpen(true)
  }

  // Função para formatar data
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('pt-BR')
    } catch {
      return dateString
    }
  }

  // Função para formatar hora
  const formatTime = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleTimeString('pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
    } catch {
      return dateString
    }
  }

  // Função para formatar valor monetário
  const formatCurrency = (amount: number) => formatCurrencyGTQ(amount)

  // Função para obter o texto do status
  const getStatusText = (status: string) => {
    switch (status) {
      case '1':
        return texts.statusClosed
      case '0':
        return texts.statusOpen
      case '2':
        return texts.statusCancelled
      case '3':
        return texts.statusInProgress
      default:
        return status
    }
  }

  // Função para obter a classe do status
  const getStatusClass = (status: string) => {
    switch (status) {
      case '1':
        return styles.statusClosed
      case '0':
        return styles.statusOpen
      case '2':
        return styles.statusCancelled
      case '3':
        return styles.statusInProgress
      default:
        return styles.statusClosed
    }
  }

  const handleRetry = () => {
    navigate(paths.previousClosings)
  }

  return (
    <PageContainer type="authenticated" title={texts.title}>
      <div className={styles.container}>
        {/* Loading State */}
        {loading && <Loading text="Cargando detalles..." />}

        {/* Error State */}
        {error && <Error message={error} onRetry={handleRetry} />}

        {/* Conteúdo Principal */}
        {!loading && !error && closureDetail && (
          <div className={styles.detailContainer}>
            {/* Header do Cierre */}
            <div className={styles.headerCard}>
              <div className={styles.headerTitle}>
                <h2 className={styles.title}>{texts.title}</h2>
                <span
                  className={`${styles.statusBadge} ${getStatusClass(closureDetail.status)}`}
                >
                  {getStatusText(closureDetail.status)}
                </span>
              </div>
              <div className={styles.headerSubtitle}>
                {texts.closureId}: {closureDetail.journeyId}
              </div>
            </div>

            {/* Informações do Cierre */}
            <div className={styles.infoCard}>
              <h3 className={styles.cardTitle}>{texts.closureInfo}</h3>

              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.date}</span>
                  <span className={styles.infoValue}>
                    {closureDetail.closingTime
                      ? formatDate(closureDetail.closingTime)
                      : '-'}
                  </span>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.amount}</span>
                  <span className={styles.infoValueAmount}>
                    {formatCurrency(closureDetail.totalAmount)}
                  </span>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.responsible}</span>
                  <span className={styles.infoValue}>
                    {closureDetail.userName ||
                      closureDetail.responsible ||
                      texts.notAvailable}
                  </span>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.route}</span>
                  <span className={styles.infoValue}>
                    {closureDetail.routeName || closureDetail.routeId}
                  </span>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.closingTime}</span>
                  <span className={styles.infoValue}>
                    {closureDetail.closingTime
                      ? formatTime(closureDetail.closingTime)
                      : '-'}
                  </span>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.company}</span>
                  <span className={styles.infoValue}>
                    {closureDetail.companyName}
                  </span>
                </div>
              </div>
            </div>

            {/* Informações Adicionais */}
            <div className={styles.additionalCard}>
              <h3 className={styles.cardTitle}>{texts.additionalInfo}</h3>

              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.creationDate}</span>
                  <span className={styles.infoValue}>
                    {formatDate(closureDetail.creationDate)} -{' '}
                    {formatTime(closureDetail.creationDate)}
                  </span>
                </div>

                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>{texts.lastUpdate}</span>
                  <span className={styles.infoValue}>
                    {formatDate(closureDetail.updateDate)} -{' '}
                    {formatTime(closureDetail.updateDate)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botões de Ação */}
        {!loading && !error && closureDetail && (
          <div className={styles.actionsContainer}>
            <Button className={styles.printButton} onClick={handlePrint}>
              {texts.print}
            </Button>

            <Button className={styles.backButton} onClick={handleGoBack}>
              {texts.goBack}
            </Button>
          </div>
        )}
        <PrintReceiptModal
          isOpen={printOpen}
          onClose={() => setPrintOpen(false)}
          type="journey"
          data={
            closureDetail
              ? { ...closureDetail, journeyId: closureDetail.journeyId }
              : null
          }
        />
      </div>
    </PageContainer>
  )
}
